import { CSSProperties, useContext, useState } from "react";

// material-ui
import { alpha, useTheme, Theme } from "@mui/material/styles";
import ButtonBase from "@mui/material/ButtonBase";
import CardMedia from "@mui/material/CardMedia";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

// third-party
import { Draggable, DraggingStyle, NotDraggingStyle } from "@hello-pangea/dnd";

// project imports
import useConfig from "hooks/useConfig";
import EditStory from "../Backlogs/EditStory";
import AlertItemDelete from "./AlertItemDelete";
import { openSnackbar } from "store/slices/snackbar";
import { useDispatch, useSelector } from "store";
import { selectItem, deleteItem } from "store/slices/kanban";
import { ImagePath, getImageUrl } from "utils/getImageUrl";

// assets
import MoreVertTwoToneIcon from "@mui/icons-material/MoreVertTwoTone";
import MenuBookTwoToneIcon from "@mui/icons-material/MenuBookTwoTone";

// types
import { ThemeMode } from "types/config";
import { DefaultRootStateProps } from "types";
import { KanbanItem } from "types/kanban";
import { BoardContext } from ".";

interface Props {
  item: KanbanItem;
  index: number;
}

// item drag wrapper
const getDragWrapper = (
  isDragging: boolean,
  draggableStyle: DraggingStyle | NotDraggingStyle | undefined,
  theme: Theme,
  radius: string
): CSSProperties | undefined => {
  const bgcolor =
    theme.palette.mode === ThemeMode.DARK
      ? alpha(theme.palette.background.paper, 0.9)
      : theme.palette.grey[50];
  return {
    userSelect: "none",
    margin: `0 0 ${8}px 0`,
    padding: 16,
    border: "1px solid",
    borderColor:
      theme.palette.mode === ThemeMode.DARK
        ? theme.palette.background.default
        : alpha(theme.palette.primary[200], 0.75),
    backgroundColor: isDragging ? bgcolor : theme.palette.background.paper,
    borderRadius: radius,
    ...draggableStyle,
  };
};

// ==============================|| KANBAN BOARD - ITEMS ||============================== //

const Items = ({ item, index }: Props) => {
  const { boardItemEdit, setBoardItemEdit } = useContext(BoardContext);

  const theme = useTheme();
  const dispatch = useDispatch();
  const backProfile = item?.image
    ? getImageUrl(`${item?.image}`, ImagePath.PROFILE)
    : null;

  const { borderRadius } = useConfig();
  // const kanban = useSelector((state: DefaultRootStateProps) => state.kanban);
  // const { userStory, items, columns } = kanban;

  let userStory = [
    {
      acceptance: "",
      assign: "profile-2",
      columnId: "column-4",
      commentIds: ["comment-5"],
      description:
        "Wo gegew jubva wiej pisniidi ic jarge mide ho ufcakub culuw ambupan te fezop rikic ekbet sevzog.",
      dueDate: "Mon Feb 27 2023 15:18:32 GMT+0530 (India Standard Time)",
      id: "2647",
      priority: "low",
      title: "School Management Backend",
      itemIds: ["3542", "8903", "1381"],
    },
    {
      acceptance:
        "Gid gimija ufuebjop vuji ze ir wi jih aruvihdok keubagob aguhogug uge tabu cure.",
      assign: "profile-3",
      columnId: "column-1",
      commentIds: ["comment-3"],
      description:
        "Onekane urwa joduzzij la cipebo zafuw atawa labfi hilza inepehhow inujufise hum.",
      dueDate: "Thu Feb 23 2023 15:13:31 GMT+0530 (India Standard Time)",
      id: "7833",
      priority: "high",
      title: "Inventory Implementation & Design",
      itemIds: ["4398", "3410"],
    },
    {
      acceptance:
        "Kiz ovmen fup jazaj rozibco fecki rasizsed pake redufkav joczatufo.",
      assign: "profile-3",
      columnId: "column-4",
      commentIds: ["comment-3", "comment-4"],
      description:
        "Lum vofdip keci fid fos di fikga kiinte izugfi gihude tewsadez ha niprizbef catin adahup.",
      dueDate: "Wed Feb 15 2023 10:30:11 GMT+0530 (India Standard Time)",
      id: "6940",
      priority: "medium",
      title: "Theme migration from v4 to v5",
      itemIds: ["6739", "9589", "8679"],
    },
    {
      acceptance: "Itorinceg cokmu ku pahkaavo argut.",
      assign: "profile-1",
      columnId: "column-3",
      commentIds: ["comment-4"],
      description:
        "Nar noljaw hi fusurri haodabi ewaw uc moetucu hilzirzo ca nidzerog ziw.",
      dueDate: "Tue Feb 07 2023 15:17:40 GMT+0530 (India Standard Time)",
      id: "4656",
      priority: "low",
      title: "Lunch Beauty Application",
      itemIds: ["3950", "6924"],
    },
  ];

  let columns = [
    {
      id: "column-1",
      title: "New",
      itemIds: ["3542", "8903", "1381"],
    },
    {
      id: "column-2",
      title: "Active",
      itemIds: ["6739", "3950", "6924"],
    },
    {
      id: "column-3",
      title: "Resolved",
      itemIds: ["4398"],
    },
    {
      id: "column-4",
      title: "Closed",
      itemIds: ["9589", "3410", "8679"],
    },
  ];

  let items: KanbanItem[] = [
    {
      assign: "profile-1",
      attachments: [],
      commentIds: ["comment-1"],
      description: "Content of item 1",
      dueDate: "Fri Feb 03 2023 15:20:14 GMT+0530 (India Standard Time)",
      id: "3542",
      image: "profile-back-1.png",
      priority: "low",
      title: "Online fees payment & instant announcements",
    },
    {
      assign: "profile-2",
      attachments: [],
      commentIds: ["comment-2", "comment-5"],
      description: "Content of item 2",
      dueDate: "Sat Jan 28 2023 15:23:25 GMT+0530 (India Standard Time)",
      id: "4398",
      image: false,
      priority: "high",
      title: "Creation and Maintenance of Inventory Objects",
    },
    {
      assign: "profile-3",
      attachments: [],
      description: "Content of item 3",
      dueDate: "Tue Feb 07 2023 15:24:38 GMT+0530 (India Standard Time)",
      id: "6739",
      image: false,
      priority: "low",
      title: "Update React & TypeScript version",
    },
    {
      assign: "profile-2",
      attachments: [],
      commentIds: ["comment-4"],
      description: "Content of item 4",
      dueDate: "Thu Feb 09 2023 15:25:46 GMT+0530 (India Standard Time)",
      id: "3950",
      image: "profile-back-2.png",
      priority: "low",
      title: "Set allowing rules for trusted applications.",
    },
    {
      assign: "profile-2",
      attachments: [],
      commentIds: ["comment-1", "comment-2", "comment-5"],
      description: "Content of item 5",
      dueDate: "Mon Feb 06 2023 15:26:53 GMT+0530 (India Standard Time)",
      id: "6924",
      image: "profile-back-3.png",
      priority: "medium",
      title: "Managing Applications Launch Control",
    },
    {
      assign: "profile-3",
      attachments: [],
      commentIds: ["comment-3", "comment-4"],
      description: "Content of item 6",
      dueDate: "Wed Feb 15 2023 10:30:37 GMT+0530 (India Standard Time)",
      id: "9589",
      image: false,
      priority: "medium",
      title: "Run codemods",
    },
    {
      assign: "profile-1",
      attachments: [],
      description: "Content of item 7",
      dueDate: "Mon Feb 20 2023 15:29:58 GMT+0530 (India Standard Time)",
      id: "3410",
      image: "profile-back-4.png",
      priority: "low",
      title: "Purchase Requisitions, Adjustments, and Transfers.",
    },
    {
      assign: "profile-1",
      attachments: [],
      description: "Content of item 8",
      dueDate: "Sat Mar 04 2023 15:31:05 GMT+0530 (India Standard Time)",
      id: "8903",
      image: false,
      priority: "low",
      title: "Attendance checking & homework details",
    },
    {
      assign: "profile-3",
      attachments: [],
      commentIds: ["comment-3"],
      description: "Content of item 9",
      dueDate: "Thu Feb 23 2023 15:32:13 GMT+0530 (India Standard Time)",
      id: "1381",
      image: false,
      priority: "high",
      title: "Admission, Staff & Schedule management",
    },
    {
      assign: "profile-2",
      attachments: [],
      commentIds: ["comment-5"],
      description: "Content of item 10",
      dueDate: "Mon Feb 27 2023 15:33:04 GMT+0530 (India Standard Time)",
      id: "8679",
      image: false,
      priority: "low",
      title: "Handling breaking changes",
    },
  ];

  const itemStory = userStory.filter(
    (story) => story?.itemIds?.filter((itemId) => itemId === item?.id)[0]
  )[0];

  const handlerDetails = (id: string) => {
    dispatch(selectItem(id || "1381"));
    setBoardItemEdit(true);
  };

  const [anchorEl, setAnchorEl] = useState<
    Element | (() => Element) | null | undefined
  >(null);
  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement> | undefined
  ) => {
    setAnchorEl(event?.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [open, setOpen] = useState(false);
  const handleModalClose = (status: boolean) => {
    setOpen(false);
    if (status) {
      dispatch(deleteItem(item?.id, items, columns, userStory));
      dispatch(
        openSnackbar({
          open: true,
          message: "Task Deleted successfully",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: {
            color: "success",
          },
          close: false,
        })
      );
    }
  };

  const [openStoryDrawer, setOpenStoryDrawer] = useState<boolean>(false);
  const handleStoryDrawerOpen = () => {
    setOpenStoryDrawer((prevState) => !prevState);
  };

  const editStory = () => {
    setOpenStoryDrawer((prevState) => !prevState);
  };

  return (
    <Draggable key={item?.id} draggableId={item?.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          style={getDragWrapper(
            snapshot.isDragging,
            provided.draggableProps.style,
            theme,
            `${borderRadius}px`
          )}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: itemStory ? -0.75 : 0 }}
          >
            <Typography
              onClick={() => handlerDetails(item?.id)}
              variant="subtitle1"
              sx={{
                display: "inline-block",
                width: "calc(100% - 34px)",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                verticalAlign: "middle",
                cursor: "pointer",
              }}
            >
              {item?.title}
            </Typography>

            <ButtonBase
              sx={{ borderRadius: "12px" }}
              onClick={handleClick}
              aria-controls="menu-comment"
              aria-haspopup="true"
            >
              <IconButton
                component="span"
                size="small"
                disableRipple
                aria-label="more options"
              >
                <MoreVertTwoToneIcon fontSize="inherit" />
              </IconButton>
            </ButtonBase>
            <Menu
              id="menu-comment"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleClose}
              variant="selectedMenu"
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  handleClose();
                  handlerDetails(item?.id);
                }}
              >
                Edit
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleClose();
                  setOpen(true);
                }}
              >
                Delete
              </MenuItem>
            </Menu>
            {open && (
              <AlertItemDelete
                title={item?.title}
                open={open}
                handleClose={handleModalClose}
              />
            )}
          </Stack>
          {itemStory && (
            <>
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Tooltip title="User Story">
                  <MenuBookTwoToneIcon
                    color="secondary"
                    sx={{ fontSize: "0.875rem" }}
                  />
                </Tooltip>
                <Tooltip title={itemStory.title}>
                  <Link
                    variant="caption"
                    color="secondary"
                    underline="hover"
                    onClick={editStory}
                    sx={{ cursor: "pointer", pt: 0.5 }}
                  >
                    User Story #{itemStory.id}
                  </Link>
                </Tooltip>
              </Stack>
              {/* {openStoryDrawer && (
                <EditStory
                  story={itemStory}
                  open={openStoryDrawer}
                  handleDrawerOpen={handleStoryDrawerOpen}
                />
              )} */}
            </>
          )}
          {backProfile && (
            <CardMedia
              component="img"
              image={backProfile}
              sx={{ width: "100%", borderRadius: 1, mt: 1.5 }}
              title="Slider5 image"
            />
          )}
        </div>
      )}
    </Draggable>
  );
};

export default Items;
